import { makeAutoObservable, runInAction } from 'mobx';
import { Base, t } from 'zf-web-components';
import { LightCache } from 'zf-web-components/widgets';
import { OAuthConsentService } from './service';
import type { StoreOptions } from '@hooks';
import type { RootStore } from '@stores';
import type * as Types from './types';

/** OAuth 授权确认页状态。 */
export class OAuthConsentStore {

  interaction?: Types.OAuthInteraction;
  loading = true;
  submittingDecision?: Types.OAuthDecision;
  unavailable = false;

  private readonly interactionId?: string;
  private readonly root: RootStore;
  private readonly service = new OAuthConsentService();

  constructor({ params, root }: StoreOptions<Types.OAuthConsentParams>) {
    this.interactionId = params.interaction;
    this.root = root;
    makeAutoObservable(this);
  }

  /** 加载授权交互。 */
  async onLoad(): Promise<void> {
    if (this.interactionId == null || this.interactionId === '') {
      runInAction(() => {
        this.loading = false;
        this.unavailable = true;
      });
      return;
    }
    try {
      const interaction = await this.service.getInteraction(this.interactionId);
      runInAction(() => {
        this.interaction = interaction;
      });
    } catch {
      runInAction(() => {
        this.unavailable = true;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** 提交用户决定并返回客户端回调地址。 */
  async onDecision(decision: Types.OAuthDecision): Promise<string | undefined> {
    if (this.interactionId == null || this.interaction == null || this.isSubmitting) {
      return;
    }
    this.submittingDecision = decision;
    try {
      const result = await this.service.decide({
        decision,
        decisionNonce: this.interaction.decisionNonce,
        interactionId: this.interactionId
      });
      await this.logout();
      return result.redirectUri;
    } catch {
      Base.message.error(t('授权操作失败，请重试'));
      return;
    } finally {
      runInAction(() => {
        this.submittingDecision = undefined;
      });
    }
  }

  get isAllowing(): boolean {
    return this.submittingDecision === 'allow';
  }

  get isDenying(): boolean {
    return this.submittingDecision === 'deny';
  }

  get isSubmitting(): boolean {
    return this.submittingDecision != null;
  }

  get isLoopbackRedirect(): boolean {
    return this.interaction != null
      && ['localhost', '127.0.0.1', '[::1]'].includes(this.interaction.redirectHostname);
  }

  private async logout(): Promise<void> {
    const { refreshToken, token } = this.root.local;
    const cleanupTasks: Promise<unknown>[] = [LightCache.clearAll()];
    if (token != null && refreshToken != null) {
      cleanupTasks.push(this.service.logout({ accessToken: token, refreshToken }));
    }
    await Promise.allSettled(cleanupTasks);
    runInAction(() => {
      this.root.user = undefined;
      this.root.local.token = undefined;
      this.root.local.refreshToken = undefined;
      this.root.local.expireAt = undefined;
    });
    document.cookie = 'x-access-token=; path=/; SameSite=Lax; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }

}
